import React from 'react';

import { useNavigate } from 'react-router-dom';

import { Logout, ManageAccounts } from '@mui/icons-material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import {
  Badge,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { startLogoutAsync } from '../../redux-store/auth/authSlice';
import { useAppDispatch } from '../../redux-store/hooks';
import { IIPENNotaryState } from '../../redux-store/ipenNotary/ipenNotaryTypes';
import { Status } from '../../utils/notaryStatus';

export interface IAccountNavProps {
  account: any;
  daysUntilExpiration?: number | null;
  onClose: () => void;
  accountName?: string;
  accountEmail?: string;
  roles: string;
  config?: any;
  notary?: IIPENNotaryState['notary'];
}

export interface UserClients {
  Clients: string[];
}

const StyledBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    right: 'auto',
    top: '50%',
    padding: '0 4px',
  },
}));

export default function AccountNav(props: IAccountNavProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    account,
    daysUntilExpiration,
    onClose,
    accountName,
    accountEmail,
    config,
    notary,
    roles,
  } = props;

  const handleLogout = () => {
    onClose();
    dispatch(startLogoutAsync(false));
    if (account && account.currentUser.tenant_id == 'FIAM') {
      const encAppBaseURL = encodeURIComponent(`${config.appBaseURL}logout`);
      const encLogoutRedirect = encodeURIComponent(
        `${`${config.cognitoBaseURL}`}logout?client_id=${
          config.clientId
        }&logout_uri=${encAppBaseURL}`
      );
      const logoutURL = `${`${config.eagleIdLogout}`}${encLogoutRedirect}`;
      window.localStorage.clear();

      window.location.href = logoutURL;
    } else {
      const encAppBaseURL = encodeURIComponent(`${config.appBaseURL}logout`);
      const logoutURL = `${`${config.cognitoBaseURL}`}logout?client_id=${
        config.clientId
      }&logout_uri=${encAppBaseURL}`;
      window.localStorage.clear();

      window.location.href = logoutURL;
    }
  };
  const handleProfileClick = () => {
    if (notary) {
      navigate('/notary/registration?editMode=true');
    } else {
      navigate('/notary/registration');
    }
    onClose();
  };
  /**
   * @function showNotaryProfileItem
   * Hide under the following conditions
   *  !roles.includes('Notary_Users')
   *   || notary.Status === null
   *   || notary.Status === Status.Pending
   *   || notary.Status === Status.Inactive
   * @returns boolean
   */
  const showNotaryProfileItem = () => {
    const roleJson = JSON.parse(roles.toString());
    if (
      !Object.keys(roleJson)?.includes('Notary_Users') ||
      notary == null ||
      notary?.Status === null ||
      notary?.Status === Status.Pending ||
      notary?.Status === Status.Inactive
    ) {
      return false;
    }
    return true;
  };

  /**
   * @function showAdminDashboardItem
   * Hide under the following conditions
   *  !roles.includes(Global_Users)
   *  || !roles.includes(EOEA_Users)
   * @returns boolean
   */
  const showAdminDashboardItem = () => {
    const roleJson = JSON.parse(roles.toString());
    if (
      Object.keys(roleJson)?.includes('Global_Users') ||
      Object.keys(roleJson)?.includes('EOEA_Users')
    ) {
      return true;
    }
    return false;
  };

  const commissionExpirationText = (daysUntilExpiration: number) => {
    if (daysUntilExpiration > 3) {
      return 'Commission expires soon';
    } else if (daysUntilExpiration <= 3 && daysUntilExpiration > 0) {
      return `Commission expires in ${daysUntilExpiration} day${
        daysUntilExpiration > 1 ? 's' : ''
      }`;
    } else {
      return 'Commission expired';
    }
  };

  const handleNavigateToAdminDashboard = () => {
    onClose();
    navigate('/admin');
  };

  return (
    <List component='nav'>
      <ListItem>
        <ListItemText
          primary={
            <Typography
              variant='h6'
              sx={{ fontWeight: 400, color: 'primary.main' }}
              component='span'>
              {accountName}
            </Typography>
          }
          secondary={accountEmail}
        />
      </ListItem>
      {showNotaryProfileItem() && (
        <ListItemButton component='a' onClick={handleProfileClick}>
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: 2,
            }}>
            <ManageAccounts />
          </ListItemIcon>
          <ListItemText
            primary='Notary Profile'
            secondary={
              daysUntilExpiration && daysUntilExpiration <= 30 ? (
                <StyledBadge
                  badgeContent=''
                  variant='dot'
                  color={daysUntilExpiration <= 0 ? 'error' : 'warning'}>
                  <Typography
                    sx={{ display: 'inline', pl: 2, whiteSpace: 'nowrap' }}
                    component='span'
                    variant='body2'>
                    {commissionExpirationText(daysUntilExpiration)}
                  </Typography>
                </StyledBadge>
              ) : (
                ''
              )
            }
          />
        </ListItemButton>
      )}
      {showAdminDashboardItem() && (
        <ListItemButton onClick={handleNavigateToAdminDashboard}>
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: 2,
            }}>
            <DashboardOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary='Admin Dashboard' />
        </ListItemButton>
      )}
      <ListItemButton onClick={handleLogout}>
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: 2,
          }}>
          <Logout />
        </ListItemIcon>
        <ListItemText primary='Logout' />
      </ListItemButton>
    </List>
  );
}
